import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";

const STATUS_COLOR = { Open: "bg-primary/15 text-primary", Answered: "bg-accent/15 text-accent", Closed: "bg-muted text-muted-foreground" };

export default function PortalTickets() {
  const qc = useQueryClient();
  const { data: tickets = [] } = useQuery({ queryKey: ["portal-tickets"], queryFn: async () => (await api.get("/portal/tickets")).data });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ subject: "", category: "General", priority: "Medium", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/portal/tickets", form);
      toast.success("Ticket created");
      setOpen(false); setForm({ subject: "", category: "General", priority: "Medium", message: "" });
      qc.invalidateQueries({ queryKey: ["portal-tickets"] });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="font-display text-2xl font-bold">Support tickets</h1><p className="text-sm text-muted-foreground">Get help from our team.</p></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full" data-testid="new-ticket-btn"><Plus className="mr-2 h-4 w-4" /> New ticket</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create a support ticket</DialogTitle>
              <DialogDescription>Describe your issue or request and our team will respond by email and in your portal.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4" data-testid="ticket-form">
              <div><Label>Subject</Label><Input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1.5" data-testid="ticket-subject" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>{["General", "Technical", "Billing", "Project", "Feature request"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>{["Low", "Medium", "High", "Urgent"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Message</Label><Textarea required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5" data-testid="ticket-message" /></div>
              <Button type="submit" className="w-full rounded-full" disabled={busy} data-testid="ticket-submit">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create ticket</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-6 space-y-3">
        {tickets.length === 0 && <p className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">No tickets yet.</p>}
        {tickets.map((t) => (
          <Link key={t.id} to={`/dashboard/tickets/${t.id}`} className="flex items-center justify-between bs-card p-5 hover:shadow-soft" data-testid={`ticket-${t.id}`}>
            <div>
              <div className="flex items-center gap-2"><span className="font-mono text-xs text-muted-foreground">{t.ticket_no}</span><Badge variant="outline">{t.category}</Badge></div>
              <h3 className="mt-1 font-display font-bold">{t.subject}</h3>
            </div>
            <Badge className={STATUS_COLOR[t.status] || ""}>{t.status}</Badge>
          </Link>
        ))}
      </div>
    </div>
  );
}
